import Customers from 'api-client/Customers'
import { EventBus } from '@/plugins/event-bus'
import { extend } from 'lodash'
import CustomerSearchType from '@/helpers/enums/customerSearchType.js'
import CustomerSearchHelper from '@/helpers/CustomerSearchHelper'
import {
  mapVehicleInfoToExchangeCar as mappers_mapVehicleInfoToExchangeCar,
  getUnknownExchangeCar as mappers_getUnknownExchangeCar,
} from '@/mappers/ExchangeCarMapper'
import Vehicle from 'api-client/Vehicle'

const moduleState = {
  customersSearchResult: [],
  vehicleCustomersSearchResult: [],
  recommendedCustomers: [],
  customerVehicleInformation: {},
  customerVehicleInformationExtended: {},
  query: '',
  showModal: false,
}

const getters = {
  customersSearchResults: (state) => state.customersSearchResult,
  vehicleCustomersSearchResult: (state) => state.vehicleCustomersSearchResult,
  recommendedCustomers: (state) => state.recommendedCustomers,
  searchQuery: (state) => state.query,
  showModal: (state) => state.showModal,
  customerVehicleInformation: (state) => state.customerVehicleInformation,
  customerVehicleInformationExtended: (state) => state.customerVehicleInformationExtended,
}

const mutations = {
  setCustomerDetail: (state, payload) => {
    var customer = state.customersSearchResult?.find(
      (x) => x.CustomerMagicNumber === payload.CustomerMagicNumber
    )
    if (customer) {
      customer = extend(customer, payload)
    }

    customer = state.vehicleCustomersSearchResult?.find(
      (x) => x.CustomerMagicNumber === payload.CustomerMagicNumber
    )

    if (customer) {
      customer = extend(customer, payload)
    }
  },
  setCustomersSearchResult: (state, customers) => {
    state.customersSearchResult = customers
    EventBus.$emit('customer/customersSearchResults')
  },
  setVehicleCustomersSearchResult: (state, customers) => {
    state.vehicleCustomersSearchResult = customers
    EventBus.$emit('customer/vehicleCustomersSearchResult')
  },
  setRecommendedCustomers: (state, recommendedCustomers) => {
    state.recommendedCustomers = recommendedCustomers
    EventBus.$emit('customer/recommendedCustomers')
  },
  setSearchQuery: (state, query) => {
    state.query = query
  },
  setCustomerVehicleInformation: (state, customerVehicleInformation) => {
    state.customerVehicleInformation = customerVehicleInformation
    EventBus.$emit('customer/customerVehicleInformation')
  },
  setCustomerVehicleInformationExtended: (state, customerVehicleInformationExtended) => {
    state.customerVehicleInformationExtended = customerVehicleInformationExtended
    EventBus.$emit('customer/customerVehicleInformationExtended')
  },
}

const actions = {
  async searchDealerCustomers({ commit }, query) {
    try {
      const results = await Customers.searchDealerCustomers(query)
      commit('setCustomersSearchResult', results)
    } catch (error) {
      commit('setCustomersSearchResult', [])
      throw error
    }
  },
  async searchDealerCustomersForVehicle({ commit }, query) {
    const results = await Customers.searchDealerCustomers(query)
    commit('setVehicleCustomersSearchResult', results)
  },
  async searchCompanyRelations({ commit }, customerCompanyMagicNumber) {
    const results = await Customers.searchCompanyRelations(customerCompanyMagicNumber)
    commit('setCustomersSearchResult', results)
  },
  async searchRelationsByLicensePlate({ commit }, query) {
    try {
      const resultsFull = await Customers.searchRelationsByLicensePlate(query)
      const resultsCustomers = resultsFull.AutolineCustomerRelations
      commit(
        'setCustomersSearchResult',
        resultsCustomers
          ? resultsCustomers.map((x) => {
              return {
                CustomerMagicNumber: x.Reference,
                CustomerFirstName: x.Name,
                CustomerAddress: x.Address,
                CustomerRelation: x.RelationType,
                AutolineCustomerType: x.CustomerType,
              }
            })
          : []
      )
      commit('setCustomerVehicleInformation', resultsFull.VehicleInformation)
    } catch (error) {
      commit('setCustomersSearchResult', [])
      throw error
    }
  },
  async searchRelationsByLicensePlateForVehicle({ commit }, query) {
    const resultsFull = await Customers.searchRelationsByLicensePlate(query)
    const resultsCustomers = resultsFull.AutolineCustomerRelations
    commit(
      'setVehicleCustomersSearchResult',
      resultsCustomers
        ? resultsCustomers.map((x) => {
            return {
              CustomerMagicNumber: x.Reference,
              CustomerFirstName: x.Name,
              CustomerAddress: x.Address,
              CustomerRelation: x.RelationType,
              AutolineCustomerType: x.CustomerType,
            }
          })
        : []
    )
  },
  async getCustomerByMagicNumber({ commit }, magicNumber) {
    if (magicNumber) {
      const result = await Customers.getCustomerByMagicNumber({
        magicNumber: magicNumber,
        type: CustomerSearchType.customer,
      })
      commit('setCustomerDetail', result)
    }
  },
  async searchCompanyByMagicNumberInCustomerSearch({ commit }, magicNumber) {
    if (magicNumber) {
      let result = await Customers.getCustomerByMagicNumber({
        magicNumber: magicNumber,
        type: CustomerSearchType.company,
      })
      result = CustomerSearchHelper.CompanyToCustomerExtend(result)
      commit('setCustomerDetail', result)
    }
  },
  async searchRecommendedDealerCustomers({ commit }, customerCompanyMagicNumber) {
    const results = await Customers.searchCompanyRelations(customerCompanyMagicNumber)
    commit('setCustomersSearchResult', results)
    commit('setRecommendedCustomers', results)
  },
  resetSearchResults({ commit }) {
    commit('setCustomersSearchResult', [])
    commit('setRecommendedCustomers', [])
    commit('setVehicleCustomersSearchResult', [])
    commit('setCustomerVehicleInformation', {})
    commit('setCustomerVehicleInformationExtended', {})
  },
  async searchCustomer({ commit }, results) {
    commit('setCustomersSearchResult', results)
  },
  setCommonDrivers({ commit }, commonDrivers) {
    commit('setCustomersSearchResult', commonDrivers)
  },
  setSearchQuery({ commit }, query) {
    commit('setSearchQuery', query)
  },
  async updateAccount({ state }, { customer, dealerId, quoteId }) {
    return await Customers.updateAccount(customer, dealerId, quoteId)
  },
  async getCarInfo({ commit }, payload) {
    try {
      if (!payload) {
        console.warn('getCarInfo payload was null or not defined')
        return
      }

      const vehicleInfoResponse = await Vehicle.getVehicleInfo(payload)
      const car = mappers_mapVehicleInfoToExchangeCar(vehicleInfoResponse.vehicle)
      commit('setCustomerVehicleInformationExtended', car)
    } catch (error) {
      console.error('Error in getCarInfo:', error)
      const car = mappers_getUnknownExchangeCar(payload)
      commit('setCustomerVehicleInformationExtended', car)
    }
    return Promise.resolve()
  },
}

export default {
  state: moduleState,
  getters,
  mutations,
  actions,
  namespaced: true,
}
